// test start ENG-18988 prevCareCompare
// packages
import React from "react";
import kebabCase from "lodash/kebabCase";

// media
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faCheck, faMinus } from "@fortawesome/pro-regular-svg-icons";

// utils
import { cn } from "@/shared/utils";
import Strings from "@/shared/utils/Strings.constants";

interface PlanToggleButtonProps {
    /** Tier name, e.g. "Platinum". Appended to the visible label from `lg` only. */
    label: string;
    isActive: boolean;
    onToggle: () => void;
    /** True while a quote update is in flight — blocks double-submits and out-of-order toggles. */
    disabled?: boolean;
}

const { ADD, ADDED, REMOVE } = Strings.PREVENTIVE_COMPARE;

/**
 * D7/D8 — the plan column's select control. Four states:
 *   default · hover-unselected · selected · hover-selected (which reads "Remove", since that is
 *   what the click does). The accessible name leads with the visible text so it stays a superset
 *   of what's rendered, and `aria-pressed` carries the selection for assistive tech.
 */
export function PlanToggleButton({ label, isActive, onToggle, disabled }: PlanToggleButtonProps) {
    return (
        <button
            type="button"
            data-testid={`preventive-compare-toggle-${kebabCase(label)}`}
            aria-pressed={isActive}
            aria-label={isActive ? `${ADDED}, ${REMOVE.toLowerCase()} ${label}` : `${ADD} ${label}`}
            disabled={disabled}
            onClick={onToggle}
            className={cn(
                "group/toggle flex h-9 w-full items-center justify-center gap-1 rounded-full border-2 px-1 text-xs font-medium shadow-sm transition-all duration-200 hover:scale-105 hover:shadow-lg disabled:pointer-events-none disabled:opacity-50 lg:h-11 lg:gap-2 lg:px-4 lg:text-base",
                isActive
                    ? "border-stroke-transparent bg-background-brand-secondary text-content-primary-invert hover:bg-background-danger-secondary"
                    : // content-brand on the hover tint is only 3.83:1 at 12px (normal-size text needs 4.5:1);
                      // content-brand-secondary is 6.02:1 and is the text token the states matrix ships with.
                      "border-stroke-brand bg-background-primary text-content-brand hover:bg-background-brand-weakest hover:text-content-brand-secondary"
            )}
        >
            {isActive ? (
                <>
                    <span className="flex items-center gap-1 group-hover/toggle:hidden lg:gap-2">
                        <FontAwesomeIcon icon={faCheck} className="size-4" />
                        {ADDED}
                    </span>
                    <span className="hidden items-center gap-1 group-hover/toggle:flex lg:gap-2">
                        <FontAwesomeIcon icon={faMinus} className="size-4" />
                        {REMOVE}
                    </span>
                </>
            ) : (
                <>
                    <FontAwesomeIcon icon={faPlus} className="size-4" />
                    <span className="whitespace-nowrap">
                        {ADD}
                        <span className="hidden lg:inline"> {label}</span>
                    </span>
                </>
            )}
        </button>
    );
}

export default PlanToggleButton;
// test end
